import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Avatar,
    Chip,
    TablePagination,
    TextField,
    InputAdornment,
    LinearProgress,
    IconButton,
    Menu,
    MenuItem,
    TableSortLabel,
    Grid
} from '@mui/material';
import {
    Search,
    MoreVert,
    TrendingUp,
    AccountBalance,
    CreditCard,
    Assessment,
    Download,
    FilterList,
    Person
} from '@mui/icons-material';
import api from "../../api/api";
import translationsMap from "../../en-ja-map"; // <-- import your map

const t = (key) => translationsMap?.[key] ?? key; // <-- same helper you used elsewhere

export default function BillingsPayment() {
    const [activeTab, setActiveTab] = useState('payments');
    const [statsData, setStatsData] = useState(null);
    const [paymentsData, setPaymentsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredPayments, setFilteredPayments] = useState([]);
    const [orderBy, setOrderBy] = useState('date');
    const [order, setOrder] = useState('desc');
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedPaymentId, setSelectedPaymentId] = useState(null);

    const user = JSON.parse(localStorage.getItem('user')) || {};

    const getCardsData = () => {
        api.get(`/v1/clinic/payment/stats/${user?.id}`)
            .then((response) => {
                console.log('Cards Data:', response.data);
                setStatsData(response.data);
            })
            .catch((error) => {
                console.error('Error fetching cards data:', error);
                setStatsData(null);
            });
    }

    const getBillingsData = () => {
        api.get(`/v1/clinic/user/paymet-history/${user?.id}`)
            .then((response) => {
                console.log('Billings Data:', response.data);
                setPaymentsData(response.data);
                setFilteredPayments(response.data?.payments || []);
            })
            .catch((error) => {
                console.error('Error fetching billings data:', error);
                setPaymentsData(null);
                setFilteredPayments([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    useEffect(() => {
        if (user?.id) {
            getCardsData();
            getBillingsData();
        } else {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!paymentsData?.payments) return;
        let filtered = [...paymentsData.payments];
        if (searchTerm) {
            filtered = filtered.filter(payment =>
                payment.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                payment.order_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                payment.method.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        setFilteredPayments(filtered);
    }, [paymentsData, searchTerm]);

    const formatCurrency = (amount) => {
        return `¥${amount.toLocaleString()}`;
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }) + ' ' + date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'success';
            case 'pending': return 'warning';
            case 'failed': return 'error';
            default: return 'default';
        }
    };

    const handleSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const sortedPayments = filteredPayments.sort((a, b) => {
        if (orderBy === 'date') {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return order === 'asc' ? dateA - dateB : dateB - dateA;
        }
        if (orderBy === 'amount') {
            return order === 'asc' ? a.amount - b.amount : b.amount - a.amount;
        }
        if (orderBy === 'user_name') {
            return order === 'asc'
                ? a.user_name.localeCompare(b.user_name)
                : b.user_name.localeCompare(a.user_name);
        }
        return 0;
    });

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleActionClick = (event, paymentId) => {
        setAnchorEl(event.currentTarget);
        setSelectedPaymentId(paymentId);
    };

    const handleActionClose = () => {
        setAnchorEl(null);
        setSelectedPaymentId(null);
    };

    const generateAvatar = (name) => {
        const nameParts = name?.split(' ');
        if (nameParts?.length >= 2) {
            return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
        }
        return name?.substring(0, 2).toUpperCase();
    };

    const paginatedPayments = sortedPayments.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const stats = [
        {
            title: t('Total Processed'),
            value: statsData?.totalAmount || 0,
            icon: TrendingUp
        },
        {
            title: t('Pending Payments'),
            value: statsData?.pending || 0,
            icon: AccountBalance
        },
        {
            title: t('Failed Payments'),
            value: statsData?.failed || 0,
            icon: CreditCard
        },
        {
            title: t('Success Rate'),
            value: statsData?.successRate || '0%',
            icon: Assessment,
            isPercentage: true
        }
    ];

    // Build "Showing X-Y of Z" like on your other screen
    const rangeFrom = Math.min(page * rowsPerPage + 1, sortedPayments.length);
    const rangeTo = Math.min((page + 1) * rowsPerPage, sortedPayments.length);
    const total = sortedPayments.length;
    const rangeText = (translationsMap?.["Showing {{from}}-{{to}} of {{total}}"] ?? "Showing {{from}}-{{to}} of {{total}}")
        .replace("{{from}}", rangeFrom)
        .replace("{{to}}", rangeTo)
        .replace("{{total}}", total);

    return (
        <Box sx={{
            p: 3,
            maxWidth: '100%',
            backgroundColor: 'background.default',
            minHeight: '100vh'
        }}>

            {/* Stats Cards - Matching DoctorManagement pattern */}
            {/* Stats Cards - Fixed to single row */}
            <Box sx={{
                display: 'flex',
                gap: 2,
                mb: 4,
                '& > *': {
                    flex: '1 1 0',
                    minWidth: '0'
                }
            }}>
                {stats.map((stat, index) => {
                    const IconComponent = stat.icon;
                    return (
                        <Paper key={index} elevation={0} sx={{ p: 2.5 }}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                mb: 1,
                            }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ fontSize: '13px', fontWeight: '500', mb: 1 }}
                                    >
                                        {stat.title}
                                    </Typography>
                                    <Typography
                                        variant="h5"
                                        fontWeight="700"
                                        color="text.primary"
                                        sx={{ fontSize: '24px', mb: 0.5 }}
                                    >
                                        {stat.isPercentage ? stat.value : formatCurrency(stat.value)}
                                    </Typography>
                                </Box>
                                <Box sx={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: '10px',
                                    backgroundColor: 'primary.main',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}>
                                    <IconComponent sx={{ color: 'white', fontSize: 20 }} />
                                </Box>
                            </Box>
                        </Paper>
                    );
                })}
            </Box>

            {/* Payment Methods & Recent Activity */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={3}>
                    <Paper elevation={0} sx={{ p: 3, height: 'fit-content' }}>
                        <Typography variant="h6" fontWeight="600" color="text.primary" sx={{ mb: 2 }}>
                            {t('Payment Methods')}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                            <Person sx={{ fontSize: '20px', color: 'text.secondary' }} />
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" fontWeight="500" color="text.primary">
                                    {t('Credit Card')}
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '13px', color: 'text.secondary' }}>
                                    {t('Primary payment method')}
                                </Typography>
                            </Box>
                            <Typography variant="body2" fontWeight="600" color="text.primary">
                                {formatCurrency(paymentsData?.totalAmount || 0)}
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={9}>
                    <Paper elevation={0} sx={{ p: 3 }}>
                        <Typography variant="h6" fontWeight="600" color="text.primary" sx={{ mb: 2 }}>
                            {t('Recent Payment Activity')}
                        </Typography>
                        {filteredPayments?.length > 0 ? (
                            <Box>
                                {filteredPayments.slice(0, 4).map((payment, index) => (
                                    <Box key={payment.payment_id} sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        py: 2, // Increased padding
                                        px: 1,
                                        borderBottom: index < 3 ? '1px solid #f0f0f0' : 'none',
                                        borderRadius: '8px',
                                        '&:hover': {
                                            backgroundColor: '#f8f9fa'
                                        }
                                    }}>
                                        <Avatar sx={{
                                            width: 40, // Slightly larger
                                            height: 40,
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            backgroundColor: 'primary.main',
                                            color: 'white'
                                        }}>
                                            {generateAvatar(payment.user_name)}
                                        </Avatar>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" fontWeight="600" color="text.primary">
                                                {payment.user_name}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary' }}>
                                                {formatDate(payment.date)}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body1" fontWeight="700" color="text.primary">
                                            {formatCurrency(payment.amount)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                {t('No recent activity')}
                            </Typography>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Main Table - Matching exact pattern from components */}
            <Paper elevation={0}>
                {loading && (
                    <Box sx={{ width: '100%', mb: 2 }}>
                        <LinearProgress />
                    </Box>
                )}

                {/* Header matching DoctorManagement exactly */}
                <Box sx={{
                    p: 3,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    background: 'linear-gradient(135deg, #fafbfc 0%, #ffffff 100%)',
                }}>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}>
                        <TextField
                            placeholder={t("Search transactions...")}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search sx={{ color: 'text.secondary', fontSize: '20px' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ width: '350px' }}
                        />
                        {/* <Button
                            startIcon={<Download />}
                            variant="contained"
                            color="primary"
                            sx={{ textTransform: 'none', fontWeight: '600' }}
                        >
                            {t('Export Data')}
                        </Button> */}
                    </Box>
                </Box>

                {/* Table exactly matching your pattern */}
                <TableContainer>
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: '600', fontSize: '12px', color: 'text.secondary', textTransform: 'uppercase' }}>#</TableCell>
                                <TableCell sx={{ fontWeight: '600', fontSize: '12px', color: 'text.secondary', textTransform: 'uppercase' }}>{t('Transaction')}</TableCell>
                                <TableCell sx={{ fontWeight: '600', fontSize: '12px', color: 'text.secondary', textTransform: 'uppercase' }}>
                                    <TableSortLabel
                                        active={orderBy === 'user_name'}
                                        direction={orderBy === 'user_name' ? order : 'asc'}
                                        onClick={() => handleSort('user_name')}
                                    >
                                        {t('Patient')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: '600', fontSize: '12px', color: 'text.secondary', textTransform: 'uppercase' }}>
                                    <TableSortLabel
                                        active={orderBy === 'amount'}
                                        direction={orderBy === 'amount' ? order : 'asc'}
                                        onClick={() => handleSort('amount')}
                                    >
                                        {t('Amount')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: '600', fontSize: '12px', color: 'text.secondary', textTransform: 'uppercase' }}>{t('Method')}</TableCell>
                                <TableCell sx={{ fontWeight: '600', fontSize: '12px', color: 'text.secondary', textTransform: 'uppercase' }}>{t('Status')}</TableCell>
                                <TableCell sx={{ fontWeight: '600', fontSize: '12px', color: 'text.secondary', textTransform: 'uppercase' }}>
                                    <TableSortLabel
                                        active={orderBy === 'date'}
                                        direction={orderBy === 'date' ? order : 'asc'}
                                        onClick={() => handleSort('date')}
                                    >
                                        {t('Date')}
                                    </TableSortLabel>
                                </TableCell>
                                {/* <TableCell sx={{ fontWeight: '600', fontSize: '12px', color: 'text.secondary', textTransform: 'uppercase' }}>{t('Actions')}</TableCell> */}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedPayments.map((payment, index) => (
                                <TableRow
                                    key={payment.payment_id}
                                    sx={{
                                        cursor: 'pointer',
                                        '&:hover': {
                                            '& .patient-avatar': {
                                                transform: 'scale(1.1)',
                                                boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}30`,
                                            },
                                            '& .patient-name': { color: 'primary.main' },
                                            backgroundColor: 'action.hover'
                                        },
                                    }}
                                >
                                    <TableCell sx={{ fontSize: '14px', color: 'text.primary', py: 2 }}>
                                        {page * rowsPerPage + index + 1}
                                    </TableCell>
                                    <TableCell sx={{ py: 2 }}>
                                        <Box>
                                            <Typography
                                                variant="body2"
                                                fontWeight="500"
                                                color="text.primary"
                                                sx={{ fontSize: '14px', mb: 0.5 }}
                                            >
                                                TXN-{payment.payment_id}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontSize: '13px', color: 'text.secondary' }}
                                            >
                                                {t('Invoice')} #{payment.order_id || `INV-${payment.payment_id}`}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ py: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar
                                                className="patient-avatar"
                                                sx={{
                                                    width: 36,
                                                    height: 36,
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    backgroundColor: 'primary.main',
                                                    color: 'white',
                                                    transition: 'all 0.2s ease',
                                                    boxShadow: (theme) => `0 2px 8px ${theme.palette.primary.main}20`,
                                                }}
                                            >
                                                {generateAvatar(payment.user_name)}
                                            </Avatar>
                                            <Typography
                                                className="patient-name"
                                                variant="body2"
                                                fontWeight="500"
                                                color="text.primary"
                                                sx={{ transition: 'color 0.2s ease' }}
                                            >
                                                {payment.user_name}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '14px', color: 'text.primary', py: 2 }}>
                                        <Typography variant="body2" fontWeight="600" color="text.primary">
                                            {formatCurrency(payment.amount)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '14px', color: 'text.primary', py: 2 }}>
                                        <Typography variant="body2" color="text.primary" sx={{ textTransform: 'capitalize' }}>
                                            {payment.method}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: 2 }}>
                                        <Chip
                                            label={payment.status}
                                            size="small"
                                            sx={{
                                                backgroundColor: payment.status === 'completed' ? 'rgba(157, 194, 20, 0.1)' :
                                                    payment.status === 'pending' ? 'rgba(255, 152, 0, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                                                color: payment.status === 'completed' ? 'success.main' :
                                                    payment.status === 'pending' ? 'warning.main' : 'error.main',
                                                fontWeight: '600',
                                                fontSize: '11px',
                                                textTransform: 'capitalize',
                                                height: '22px',
                                                '& .MuiChip-label': { px: 1 },
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '14px', color: 'text.primary', py: 2 }}>
                                        <Typography variant="body2" color="text.primary">
                                            {formatDate(payment.date)}
                                        </Typography>
                                    </TableCell>
                                    {/* <TableCell sx={{ py: 2 }}>
                                        <IconButton onClick={(e) => handleActionClick(e, payment.payment_id)}>
                                            <MoreVert />
                                        </IconButton>
                                    </TableCell> */}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Footer matching exact pattern */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 3,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    background: 'linear-gradient(135deg, #fafbfc 0%, #ffffff 100%)',
                }}>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: '14px', fontWeight: '500' }}
                    >
                        {rangeText}
                    </Typography>
                    <TablePagination
                        component="div"
                        count={sortedPayments.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        labelRowsPerPage={t("Rows per page:")}
                    />
                </Box>

                {/* Empty state matching your pattern */}
                {!loading && sortedPayments.length === 0 && (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 8,
                        px: 4,
                        mt: 4,
                        backgroundColor: 'background.paper',
                        borderRadius: '16px',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                        transition: 'all 0.3s ease',
                    }}>
                        <Box sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            backgroundColor: (theme) => `${theme.palette.primary.main}10`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 3,
                            transition: 'transform 0.3s ease',
                            '&:hover': { transform: 'scale(1.05)' },
                        }}>
                            <CreditCard sx={{ fontSize: 40, color: 'primary.main', opacity: 0.8 }} />
                        </Box>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 600,
                                color: 'text.primary',
                                mb: 1,
                                textAlign: 'center',
                                fontSize: '1.5rem',
                                letterSpacing: '-0.02em',
                            }}
                        >
                            {t('No Payment Data')}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'text.secondary',
                                textAlign: 'center',
                                maxWidth: '400px',
                                lineHeight: 1.6,
                            }}
                        >
                            {searchTerm
                                ? t('No payment transactions match your search criteria.')
                                : t('No payment transactions found for this period.')
                            }
                        </Typography>
                    </Box>
                )}
            </Paper>

            {/* Menu matching your pattern exactly */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleActionClose}
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        border: '1px solid',
                        borderColor: 'divider',
                        mt: 1,
                    },
                }}
            >
                <MenuItem
                    onClick={handleActionClose}
                    sx={{
                        py: 1.5,
                        px: 2,
                        '&:hover': {
                            backgroundColor: (theme) => `${theme.palette.primary.main}04`,
                        },
                    }}
                >
                    <Download sx={{ mr: 2, fontSize: '18px', color: 'text.secondary' }} />
                    {t('Download Receipt')}
                </MenuItem>
                <MenuItem
                    onClick={handleActionClose}
                    sx={{
                        py: 1.5,
                        px: 2,
                        '&:hover': {
                            backgroundColor: (theme) => `${theme.palette.primary.main}04`,
                        },
                    }}
                >
                    <Assessment sx={{ mr: 2, fontSize: '18px', color: 'text.secondary' }} />
                    {t('View Details')}
                </MenuItem>
            </Menu>
        </Box>
    );
}
